/**
 * 📦 **배포 직후, 아직 안 퍼진 조각을 부른 아이.**
 *
 * 화면은 조각(청크)으로 나뉘어 있고 필요할 때 받아 온다. 새로 배포한 직후에는
 * 그 조각이 **가까운 서버까지 아직 안 퍼져** 있을 수 있다. Cloudflare Pages 는 없는 경로에
 * **200 으로 index.html** 을 주기 때문에(`pages-404-cached-as-200`), 브라우저는
 * 「자바스크립트인 줄 알았는데 문서가 왔다」로 **모듈 읽기를 실패**한다.
 *
 * 그러면 아이 화면에는 **「앗, 잠깐 문제가 생겼어요」**가 뜬다. 잘못한 게 없는데.
 * 게다가 이건 **한 번 다시 열면 거의 항상 낫는다**(그 사이 조각이 퍼진다) —
 * 즉 아이에게 「다시 시도」를 누르게 할 이유가 없다. 우리가 한 번 다시 열면 된다.
 *
 * 🔴 **한 번만.** 계속 다시 열면 무한 새로고침이 된다 — 안 낫는 진짜 고장일 때
 * 아이 화면이 깜빡이기만 하는 것이 제일 나쁘다. 두 번째부터는 그냥 안내를 보여 준다.
 */

/** 브라우저마다 말이 다르다 — 셋 다 「조각을 못 받았다」이다. */
const CHUNK_SAY = [
  "failed to fetch dynamically imported module", // Chrome/Edge
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari
  "expected a javascript-or-wasm module script", // MIME 이 text/html 로 온 경우
];

/** 이 오류가 「조각을 못 받았다」인가. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = (
    (err as { message?: unknown })?.message ??
    (typeof err === "string" ? err : "")
  ).toString().toLowerCase();
  if (!msg) return false;
  return CHUNK_SAY.some((s) => msg.includes(s));
}

/**
 * 🔴 **못 받은 조각의 주소** — 그냥 다시 열어서는 안 낫는 이유가 여기 있다.
 *
 * Pages 는 아직 안 퍼진 조각에 **200 + `Cache-Control: immutable, max-age=1년`** 으로
 * index.html 을 준다(2026-09-06 실측). 브라우저는 그 **HTML 을 그 .js 주소의 답으로 1년치
 * 저장**한다. 그래서 `location.reload()` 를 해도 **제 컴퓨터 사본**을 다시 읽어 똑같이 실패한다 —
 * 조각이 서버에 다 퍼진 뒤에도 그 아이만 계속 오류 화면을 본다.
 *
 * 다시 열기 **전에** 이 주소를 `cache: "reload"` 로 한 번 받아 오면 그 사본이 새것으로 덮인다.
 * 그러려면 주소를 알아야 한다 — 브라우저가 오류 문장에 넣어 준다.
 */
export function chunkUrlFrom(err: unknown): string | null {
  const msg = ((err as { message?: unknown })?.message ?? (typeof err === "string" ? err : "")).toString();
  const m = /https?:\/\/[^\s"')]+/.exec(msg);
  if (!m) return null;
  // 조각 주소만 — 아무 주소나 다시 받아 오지 않는다(외부 입력 검증)
  return /\.(?:js|mjs)(?:\?|$)/.test(m[0]) ? m[0] : null;
}

/** 한 번 다시 열었는지 적어 두는 자리(창을 닫으면 사라진다). */
export const CHUNK_RELOAD_KEY = "gc_chunk_reload";

/**
 * 지금 다시 열어야 하나.
 *
 * @param err 잡힌 오류
 * @param mark 언제 다시 열었는지 읽고 쓰는 곳(세션 저장소). 없으면 다시 열지 않는다 —
 *             기록할 곳이 없으면 **무한 새로고침**이 되기 때문이다.
 *
 * 🔴 **「이미 해 봤다」는 방금 해 본 것만이다** (2026-10-14). 예전엔 표시를 「1」로 남기고 **안 지웠다** —
 * 같은 탭에서 두 번째 배포를 만나면 몇 시간 전의 표시 때문에 「안 낫는 고장」으로 보고 오류 화면에 멈췄다
 * (e2e chunk-after-deploy 로 재현). 이제 시각을 남기고, 그 창 안에서 또 실패할 때만 멈춘다 — 무한 새로고침은 그대로 막는다.
 */
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

export function shouldReloadForChunk(
  err: unknown,
  mark: { get: () => string | null; set: (v: string) => void } | null,
  now: number = Date.now(),
): boolean {
  if (!isChunkLoadError(err) || !mark) return false;
  const last = Number(mark.get());
  // 방금(창 안에서) 해 봤는데 또 실패 — 안 낫는 고장이다. 옛 판의 「1」 은 1970년이라 지난 것으로 본다
  if (Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_WINDOW_MS) return false;
  mark.set(String(now));
  return true;
}
